<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleWithLinesModal from '@/components/pageContent/modules/EditTitleWithLinesModal.vue';
import EditDescriptionModal from '@/components/pageContent/modules/EditDescriptionModal.vue';
import EditTitleModal from '@/components/pageContent/modules/EditTitleModal.vue';
import ImageUploadModal from '@/components/pageContent/modules/ImageUploadModal.vue';
import publishGuardMixin from '@/mixins/publishGuardMixin';
import TitleWithLinesBlock from './title-with-lines-block.vue';
import DescriptionBlock from './description-block.vue';
import styles from './wizard-steps-style.scss';

const b = bem('wizard-steps', styles);

export default {
  mixins: [publishGuardMixin],
  name: 'WizardSteps',
  components: {
    PencilIcon,
    EditTitleWithLinesModal,
    EditDescriptionModal,
    EditTitleModal,
    ImageUploadModal,
    TitleWithLinesBlock,
    DescriptionBlock,
  },
  props: {
    className: {
      type: String,
      default: '',
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      // Modal visibility states
      titleModalVisible: false,
      descriptionModalVisible: false,
      // Card editing
      editingCardIndex: null,
      cardTitleModalVisible: false,
      cardDescriptionModalVisible: false,
      cardImageModalVisible: false,
    };
  },
  computed: {
    b() {
      return b;
    },
    isMobile() {
      return this.isMobileView;
    },
    titlesList() {
      return this.data.titlesList || [];
    },
    description() {
      return this.data.description || '';
    },
    descriptionMobile() {
      return this.data.descriptionMobile || this.description;
    },
    displayDescription() {
      return this.isMobile ? this.descriptionMobile : this.description;
    },
    hasDescription() {
      return !!this.description;
    },
    dataCards() {
      return this.data.dataForSteps || [];
    },
    // Current editing card data
    editingCard() {
      if (this.editingCardIndex === null) return null;
      return this.dataCards[this.editingCardIndex] || null;
    },
  },
  methods: {
    // Get card display values based on view mode
    getCardTitle(card) {
      return this.isMobile && card.mobTitle ? card.mobTitle : card.title || '';
    },
    getCardDescription(card) {
      return this.isMobile && card.mobDescription ? card.mobDescription : card.description || '';
    },
    getCardImage(card) {
      return this.isMobile && card.mobSrc ? card.mobSrc : card.src || '';
    },
    // Modal open methods
    openTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.titleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.descriptionModalVisible = true;
      }
    },
    openCardTitleModal(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.editingCardIndex = index;
        this.cardTitleModalVisible = true;
      }
    },
    openCardDescriptionModal(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.editingCardIndex = index;
        this.cardDescriptionModalVisible = true;
      }
    },
    openCardImageModal(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.editingCardIndex = index;
        this.cardImageModalVisible = true;
      }
    },
    // Save handlers
    handleTitleSave(values) {
      this.$emit('update', { titlesList: values });
      this.titleModalVisible = false;
    },
    handleTitleDelete() {
      this.$emit('update', { titlesList: [] });
      this.titleModalVisible = false;
    },
    handleDescriptionSave(values) {
      this.$emit('update', {
        description: values.description,
        descriptionMobile: values.descriptionMobile,
      });
      this.descriptionModalVisible = false;
    },
    handleDescriptionDelete() {
      this.$emit('update', { description: '', descriptionMobile: '' });
      this.descriptionModalVisible = false;
    },
    // Card handlers
    handleCardTitleSave(values) {
      if (this.editingCardIndex === null) return;
      const newCards = [...this.dataCards];
      newCards[this.editingCardIndex] = {
        ...newCards[this.editingCardIndex],
        title: values.title,
        mobTitle: values.titleMobile,
      };
      this.$emit('update', { dataForSteps: newCards });
      this.cardTitleModalVisible = false;
      this.editingCardIndex = null;
    },
    handleCardTitleDelete() {
      if (this.editingCardIndex === null) return;
      const newCards = [...this.dataCards];
      newCards[this.editingCardIndex] = {
        ...newCards[this.editingCardIndex],
        title: '',
        mobTitle: '',
      };
      this.$emit('update', { dataForSteps: newCards });
      this.cardTitleModalVisible = false;
      this.editingCardIndex = null;
    },
    handleCardDescriptionSave(values) {
      if (this.editingCardIndex === null) return;
      const newCards = [...this.dataCards];
      newCards[this.editingCardIndex] = {
        ...newCards[this.editingCardIndex],
        description: values.description,
        mobDescription: values.descriptionMobile,
      };
      this.$emit('update', { dataForSteps: newCards });
      this.cardDescriptionModalVisible = false;
      this.editingCardIndex = null;
    },
    handleCardDescriptionDelete() {
      if (this.editingCardIndex === null) return;
      const newCards = [...this.dataCards];
      newCards[this.editingCardIndex] = {
        ...newCards[this.editingCardIndex],
        description: '',
        mobDescription: '',
      };
      this.$emit('update', { dataForSteps: newCards });
      this.cardDescriptionModalVisible = false;
      this.editingCardIndex = null;
    },
    handleCardImageSave(values) {
      if (this.editingCardIndex === null) return;
      const newCards = [...this.dataCards];
      newCards[this.editingCardIndex] = {
        ...newCards[this.editingCardIndex],
        src: values.image || newCards[this.editingCardIndex].src,
        mobSrc: values.imageMobile || newCards[this.editingCardIndex].mobSrc,
      };
      this.$emit('update', { dataForSteps: newCards });
      this.cardImageModalVisible = false;
      this.editingCardIndex = null;
    },
    handleCardImageDelete() {
      if (this.editingCardIndex === null) return;
      const newCards = [...this.dataCards];
      newCards[this.editingCardIndex] = {
        ...newCards[this.editingCardIndex],
        src: '',
        mobSrc: '',
      };
      this.$emit('update', { dataForSteps: newCards });
      this.cardImageModalVisible = false;
      this.editingCardIndex = null;
    },
  },
};
</script>

<template>
  <section :class="b({ mix: className, isMobileView, isEditMode })">
    <div :class="b('container', { isMobileView })">
      <!-- Title (required) -->
      <div
        :class="b('title-wrap', { isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openTitleModal"
        @keyup.enter="openTitleModal"
        @keyup.space="openTitleModal"
      >
        <TitleWithLinesBlock
          v-if="titlesList.length"
          :class="b('title', { isMobileView })"
          :is-mobile="isMobile"
          :is-mobile-view="isMobileView"
          :text-list="titlesList"
        />
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add title
        </div>
        <PencilIcon
          v-if="isEditMode && titlesList.length"
          :class="b('edit-icon')"
          color="#293241"
          :size="20"
        />
      </div>

      <!-- Description (optional) -->
      <div
        v-if="hasDescription || isEditMode"
        :class="b('description-wrap', { isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openDescriptionModal"
        @keyup.enter="openDescriptionModal"
        @keyup.space="openDescriptionModal"
      >
        <DescriptionBlock
          v-if="hasDescription"
          :class="b('description', { isMobileView })"
          :is-mobile-view="isMobileView"
        >
          {{ displayDescription }}
        </DescriptionBlock>
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add description
        </div>
        <PencilIcon
          v-if="isEditMode && hasDescription"
          :class="b('edit-icon')"
          color="#293241"
          :size="18"
        />
      </div>

      <!-- Cards wrapper -->
      <div :class="b('wrapper', { isMobileView })">
        <div :class="b('card-wrap', { isMobileView })">
          <div
            v-for="(card, index) in dataCards"
            :key="card.id || index"
            :class="b('card', { isMobileView })"
          >
            <!-- Card Image -->
            <div
              :class="b('card-image-area', { isMobileView, editable: isEditMode })"
              :role="isEditMode ? 'button' : undefined"
              :tabindex="isEditMode ? 0 : undefined"
              @click="openCardImageModal(index)"
              @keyup.enter="openCardImageModal(index)"
              @keyup.space="openCardImageModal(index)"
            >
              <div v-if="getCardImage(card)" :class="b('card-image', { isMobileView })">
                <img
                  :src="getCardImage(card)"
                  :alt="getCardTitle(card)"
                />
              </div>
              <div v-else-if="isEditMode" :class="b('image-placeholder', { isMobileView })">
                <span :class="b('placeholder-plus')">+</span>
                <span :class="b('placeholder-text')">CLICK TO UPLOAD</span>
              </div>
              <PencilIcon
                v-if="isEditMode && getCardImage(card)"
                :class="b('card-image-edit-icon')"
                color="#ffffff"
                :size="18"
              />
            </div>

            <!-- Card Title -->
            <div
              :class="b('card-title-wrap', {
                isMobileView,
                editable: isEditMode,
                even: index === 1 && isMobileView
              })"
              :role="isEditMode ? 'button' : undefined"
              :tabindex="isEditMode ? 0 : undefined"
              @click="openCardTitleModal(index)"
              @keyup.enter="openCardTitleModal(index)"
              @keyup.space="openCardTitleModal(index)"
            >
              <h3
                v-if="getCardTitle(card)"
                :class="b('card-title', { isMobileView, even: index === 1 && isMobileView })"
              >
                <template v-if="isMobileView">
                  {{ (index + 1) }}. {{ getCardTitle(card) }}
                </template>
                <template v-else>
                  {{ getCardTitle(card) }}
                </template>
              </h3>
              <div v-else-if="isEditMode" :class="b('add-field')">
                + Add card title
              </div>
              <PencilIcon
                v-if="isEditMode && getCardTitle(card)"
                :class="b('edit-icon')"
                color="#293241"
                :size="16"
              />
            </div>

            <!-- Card Description -->
            <div
              :class="b('card-desc-wrap', { isMobileView, editable: isEditMode })"
              :role="isEditMode ? 'button' : undefined"
              :tabindex="isEditMode ? 0 : undefined"
              @click="openCardDescriptionModal(index)"
              @keyup.enter="openCardDescriptionModal(index)"
              @keyup.space="openCardDescriptionModal(index)"
            >
              <p v-if="getCardDescription(card)" :class="b('card-desc', { isMobileView })">
                {{ getCardDescription(card) }}
              </p>
              <div v-else-if="isEditMode" :class="b('add-field')">
                + Add card description
              </div>
              <PencilIcon
                v-if="isEditMode && getCardDescription(card)"
                :class="b('edit-icon')"
                color="#5e5d5d"
                :size="16"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <!-- Title Modal -->
    <EditTitleWithLinesModal
      :visible="titleModalVisible"
      :data="titlesList"
      :show-delete="titlesList.length > 0"
      @save="handleTitleSave"
      @cancel="titleModalVisible = false"
      @delete="handleTitleDelete"
    />

    <!-- Description Modal -->
    <EditDescriptionModal
      :visible="descriptionModalVisible"
      :description="description"
      :description-mobile="descriptionMobile"
      @close="descriptionModalVisible = false"
      @save="handleDescriptionSave"
      @delete="handleDescriptionDelete"
    />

    <!-- Card Title Modal -->
    <EditTitleModal
      :visible="cardTitleModalVisible"
      :title="editingCard?.title || ''"
      :title-mobile="editingCard?.mobTitle || ''"
      :modal-title="`Edit Step ${editingCardIndex !== null ? editingCardIndex + 1 : ''} Title`"
      @close="cardTitleModalVisible = false; editingCardIndex = null"
      @save="handleCardTitleSave"
      @delete="handleCardTitleDelete"
    />

    <!-- Card Description Modal -->
    <EditDescriptionModal
      :visible="cardDescriptionModalVisible"
      :description="editingCard?.description || ''"
      :description-mobile="editingCard?.mobDescription || ''"
      :title="`Edit Step ${editingCardIndex !== null ? editingCardIndex + 1 : ''} Description`"
      @close="cardDescriptionModalVisible = false; editingCardIndex = null"
      @save="handleCardDescriptionSave"
      @delete="handleCardDescriptionDelete"
    />

    <!-- Card Image Modal -->
    <ImageUploadModal
      :visible="cardImageModalVisible"
      :image="editingCard?.src || ''"
      :image-mobile="editingCard?.mobSrc || ''"
      :show-delete="!!editingCard?.src"
      @close="cardImageModalVisible = false; editingCardIndex = null"
      @save="handleCardImageSave"
      @delete="handleCardImageDelete"
    />
  </section>
</template>
